import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Crown, LogOut, ListChecks, TrendingUp, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import HealthScoreCard from '../components/dashboard/HealthScoreCard';
import MyPlanTab from '../components/dashboard/MyPlanTab';
import InvestmentsTab from '../components/dashboard/InvestmentsTab';
import ChatTab from '../components/dashboard/ChatTab';
import PremiumModal from '../components/PremiumModal';

const TABS = [
  { id: 'plan', label: 'My Plan', icon: ListChecks },
  { id: 'investments', label: 'Investments', icon: TrendingUp },
  { id: 'chat', label: 'Ask Clarity', icon: MessageCircle },
];

export default function Dashboard() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('plan');
  const [showPremium, setShowPremium] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/me');
      setMe(data);
    } catch (e) {
      console.error('Failed to load profile:', e);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate('/auth');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) fetchMe();
  }, [user, fetchMe]);

  useEffect(() => {
    if (user && searchParams.get('premium') === '1') {
      setShowPremium(true);
      setSearchParams({}, { replace: true });
    }
  }, [user, searchParams, setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !user || pollRef.current) return;
    pollRef.current = true;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) {
        toast.error('Payment check timed out. Contact support if you were charged.');
        setSearchParams({}, { replace: true });
        return;
      }
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          toast.success('Payment successful — welcome to Clarity Premium!');
          setSearchParams({}, { replace: true });
          fetchMe();
          setRefreshKey((k) => k + 1);
          return;
        }
        if (data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setSearchParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    toast.info('Checking payment status...');
    poll();
  }, [user, searchParams, setSearchParams, fetchMe]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const onDataChange = useCallback(() => {
    setRefreshKey((k) => k + 1);
    fetchMe();
  }, [fetchMe]);

  if (loading || !user) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-[#5C5C66]">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F6F6F9]">
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EB] py-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 flex items-center justify-between gap-2">
          <h1 className="text-2xl font-black tracking-tighter cursor-pointer" onClick={() => navigate('/')} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clarity
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            {me?.is_premium ? (
              <span data-testid="premium-badge" className="inline-flex items-center gap-1.5 bg-[#534AB7]/10 text-[#534AB7] rounded-full px-3 py-1.5 text-xs sm:text-sm font-semibold">
                <Crown size={14} />
                {me.founding_member ? 'Founding Member' : 'Premium'}
              </span>
            ) : (
              <button
                data-testid="upgrade-btn"
                onClick={() => setShowPremium(true)}
                className="inline-flex items-center gap-1.5 bg-[#534AB7] text-white hover:bg-[#423B99] rounded-full px-4 py-2 text-xs sm:text-sm font-semibold transition-colors"
              >
                <Crown size={14} />
                Upgrade
              </button>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleSignOut}
              className="text-[#5C5C66] hover:text-[#0A0A0B] p-2 rounded-lg hover:bg-[#F6F6F9] transition-colors"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <p className="text-sm text-[#5C5C66] mb-1">Welcome back</p>
          <h2 data-testid="dashboard-email" className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0B] break-all">
            {me?.email || user.email}
          </h2>
        </div>

        <HealthScoreCard refreshKey={refreshKey} hasPlan={me?.has_plan} />

        <div className="flex gap-2 mt-8 mb-6 overflow-x-auto pb-1" data-testid="dashboard-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === id ? 'bg-[#534AB7] text-white' : 'bg-white text-[#5C5C66] border border-[#E5E5EB] hover:border-[#534AB7]'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {tab === 'plan' && <MyPlanTab me={me} onDataChange={onDataChange} onUpgrade={() => setShowPremium(true)} />}
        {tab === 'investments' && <InvestmentsTab onUpgrade={() => setShowPremium(true)} />}
        {tab === 'chat' && <ChatTab me={me} onUpgrade={() => setShowPremium(true)} onUsage={fetchMe} />}
      </main>

      <PremiumModal open={showPremium} onClose={() => setShowPremium(false)} />
    </div>
  );
}
